/**
 * Shared nested resource summary renderer for drawer/table cells.
 *
 * It owns the compact Category -> Product -> Variant display used by activity
 * panels, including optional per-variant metadata returned by backend payloads.
 */
import { ChangeDetectionStrategy, Component, input, signal } from '@angular/core';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

export interface UiResourceSummaryVariantItem {
  id: string;
  name: string;
  quantity?: number;
  unit?: string;
  sellingPrice?: number | null;
  productReference?: string;
}

export interface UiResourceSummaryProductItem {
  id: string;
  name: string;
  variants: UiResourceSummaryVariantItem[];
}

export interface UiResourceSummaryCategoryItem {
  id: string;
  name: string;
  products: UiResourceSummaryProductItem[];
}

/**
 * COMPONENT: Ui Resource Summary
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-resource-summary',
  standalone: true,
  imports: [UiLabelTranslatePipe],
  templateUrl: './ui-resource-summary.component.html',
  styleUrl: './ui-resource-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiResourceSummaryComponent {
  readonly items = input<UiResourceSummaryCategoryItem[]>([]);
  readonly expandedProductIds = signal<string[]>([]);

  /**
   * BUSINESS LOGIC: toggleProduct
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  toggleProduct(productKey: string): void {
    this.expandedProductIds.update((ids) => (
      ids.includes(productKey)
        ? ids.filter((id) => id !== productKey)
        : [...ids, productKey]
    ));
  }

  /**
   * BUSINESS LOGIC: isExpanded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  isExpanded(productKey: string): boolean {
    return this.expandedProductIds().includes(productKey);
  }

  /**
   * BUSINESS LOGIC: productKey
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  productKey(categoryId: string, productId: string): string {
    return `${categoryId}:${productId}`;
  }

  /**
   * BUSINESS LOGIC: formatQuantity
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  formatQuantity(variant: UiResourceSummaryVariantItem): string {
    const quantity = Number(variant.quantity ?? 0).toLocaleString();
    const unit = variant.unit?.trim();
    return unit ? `${quantity} ${unit}` : quantity;
  }

  /**
   * BUSINESS LOGIC: formatSellingPrice
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  formatSellingPrice(value: number | null | undefined): string {
    return value === undefined || value === null ? '—' : Number(value).toLocaleString();
  }
}
